/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import {
  Button,
  Description,
  FlexContainer,
  Input,
  Spinner,
  Text,
  theme,
} from "@nordcloud/gnui";
import { UpdateWorkflowInput } from "~/generated/graphql";
import { mapInputStatus } from "~/tools";
import { DisplayText } from "./DisplayText";
import { getWorkflowDetailsEditSchema } from "./validators";

type Props = {
  initValue?: string | null;
  property: "description" | "name";
  required?: boolean;
  onSubmit: SubmitHandler<UpdateWorkflowInput>;
};

export function WorkflowDetailsInlineText({
  initValue,
  property,
  required = true,
  onSubmit,
}: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(getWorkflowDetailsEditSchema()),
  });
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const doSubmit = async (data: UpdateWorkflowInput) => {
    setIsEditMode(false);
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const EditForm = (
    <form css={{ display: "block" }} onSubmit={handleSubmit(doSubmit)}>
      <FlexContainer>
        <Input
          type="text"
          data-testid="text-input"
          {...register(property, { required })}
          small
          status={mapInputStatus(errors[property] ? "error" : undefined)}
          defaultValue={initValue ?? ""}
        />
        <Button
          ml={theme.spacing.spacing02}
          type="submit"
          icon="save"
          size="md"
          severity="low"
        >
          Save
        </Button>
        <Button
          ml={theme.spacing.spacing02}
          icon="close"
          size="md"
          severity="low"
          onClick={() => {
            setIsEditMode(false);
          }}
        >
          Cancel
        </Button>
      </FlexContainer>
      <When condition={errors[property]}>
        <Description>{errors[property]?.message}</Description>
      </When>
    </form>
  );

  if (loading) {
    return (
      <>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" ml={theme.spacing.spacing04}>
          Loading...
        </Text>
      </>
    );
  }

  if (isEditMode) {
    return EditForm;
  }

  return (
    <FlexContainer alignItems="center">
      <DisplayText
        content={initValue ?? ""}
        onChange={() => {
          setIsEditMode(true);
        }}
      />
    </FlexContainer>
  );
}
